/**
 * BackendService - Wrapper around backend modules for React components
 * Provides a clean API that integrates PocketBase with existing backend logic
 * Requirements: 10.1, 10.2, 10.3, 10.4
 */

import PocketBase from 'pocketbase';
import { POCKETBASE_URL } from './pocketbaseConfig.js';
import { ProxyClient, fetchWithRetry, CatalogCache } from '@code-burrow/tracker-core';
import DatabaseRepository from './DatabaseRepository.js';
import GameEntryManager from './backend-modules/GameEntryManager.js';
import { getOwnedGames, getGameByID, getPlayerAchievements } from './backend-modules/SteamAPIClient.js';
import { getXboxTitleHistory, getXboxGameStats } from './backend-modules/XboxAPIClient.js';
import { searchGamesByQuery } from './backend-modules/RAWGAPIClient.js';
import { GameStatus } from '../models/GameEntry.js';

const ONE_MONTH_MS = 30 * 24 * 60 * 60 * 1000;

/**
 * Infers a default status for a game based on play data.
 * Rules:
 *   - No lastPlayed or < 5 min playtime → Not played
 *   - Xbox with 100% achievement progress → Completed
 *   - lastPlayed > 1 month ago → Shelved
 *   - lastPlayed within 1 month → Playing
 */
// Statuses that sync should never overwrite (user-curated decisions)
const PROTECTED_STATUSES = new Set([
    GameStatus.COMPLETED,
    GameStatus.ABANDONED,
    GameStatus.WANT_TO_PLAY,
]);

function inferStatus({ lastPlayed, playtimeMinutes = 0, isXbox = false, achievementPct = 0 }) {
    if (!lastPlayed && playtimeMinutes < 5) return GameStatus.NOT_STARTED;
    if (isXbox && achievementPct >= 100) return GameStatus.COMPLETED;
    const lastPlayedDate = lastPlayed ? new Date(lastPlayed) : null;
    if (lastPlayedDate && lastPlayedDate < new Date(Date.now() - ONE_MONTH_MS)) return GameStatus.DIDNT_FINISH;
    if (lastPlayedDate) return GameStatus.PLAYING;
    return GameStatus.NOT_STARTED;
}

/**
 * BackendService class - Service layer for React components
 */
class BackendService {
    /**
     * Creates a new BackendService instance
     * @param {function} getToken - Clerk getToken function for authenticated proxy calls
     * @param {string} [pocketbaseUrl] - Optional PocketBase URL (defaults to config)
     */
    constructor(getToken, pocketbaseUrl = POCKETBASE_URL) {
        this.pocketbase = new PocketBase(pocketbaseUrl);
        this.proxy = new ProxyClient(getToken);
        this.database = new DatabaseRepository(this.pocketbase, this.proxy);
        this.manager = new GameEntryManager(this.database);
        this._userIdCache = new Map();
        this._gamePbIdCache = new Map();
    }

    get _apiBase() {
        return import.meta.env.VITE_PROXY_URL
            ? `${import.meta.env.VITE_PROXY_URL.replace(/\/+$/, '')}/api`
            : '/api';
    }

    async _authHeaders(currentUserId) {
        const headers = {};
        if (currentUserId) headers['x-tracker-user-id'] = currentUserId;
        if (this.proxy._getToken) {
            try {
                const token = await this.proxy._getToken();
                if (token) headers['Authorization'] = `Bearer ${token}`;
            } catch (_) {}
        }
        return headers;
    }

    async getAggregatedCollection(currentUserId) {
        const headers = await this._authHeaders(currentUserId);
        return fetchWithRetry(`${this._apiBase}/collection/videogames`, { headers });
    }

    async clearCatalogCache() {
        try { await new CatalogCache('videogames').clear(); } catch {}
    }

    async getCatalog() {
        const headers = await this._authHeaders();
        return fetchWithRetry(`${this._apiBase}/collection/videogames/catalog`, { headers });
    }

    async getUserData(currentUserId) {
        const headers = await this._authHeaders(currentUserId);
        return fetchWithRetry(`${this._apiBase}/collection/videogames/userdata`, { headers });
    }

    /**
     * Adds a game from a Steam URL
     * @param {string} url - Steam URL to parse and fetch
     * @returns {Promise<Object>} Result object { success: boolean, entry?: GameEntry, error?: string }
     * Requirements: 10.1, 10.2, 10.3, 10.4
     */
    async addGameFromURL(url) {
        try {
            const result = await this.manager.addGameFromURL(url);
            return result;
        } catch (error) {
            return {
                success: false,
                error: `Failed to add game from URL: ${error.message}`
            };
        }
    }

    /**
     * Searches for games by query string
     * @param {string} query - Search query
     * @returns {Promise<Object>} Result object { success: boolean, searchResults?: SearchResults, error?: string }
     * Requirements: 10.1, 10.2, 10.3, 10.4
     */
    async searchGames(query) {
        try {
            const result = await this.manager.addGameFromSearch(query);
            return result;
        } catch (error) {
            return {
                success: false,
                error: `Failed to search games: ${error.message}`
            };
        }
    }

    /**
     * Adds a game from search result data
     * @param {Object} gameData - Game data from search results
     * @returns {Promise<Object>} Result object { success: boolean, entry?: GameEntry, error?: string }
     * Requirements: 10.1, 10.2, 10.3, 10.4
     */
    async addGameFromData(gameData) {
        try {
            const result = await this.manager.createGameEntry(gameData);
            // The client caches the catalog for up to 5 minutes; without this a game that
            // was just added isn't in it and never renders until the cache expires.
            if (result.success) await this.clearCatalogCache();
            return result; // includes { success, entry, pbId }
        } catch (error) {
            return {
                success: false,
                error: `Failed to add game from data: ${error.message}`
            };
        }
    }

    /**
     * Gets all game entries from the database
     * @returns {Promise<Object>} Result object { success: boolean, entries?: GameEntry[], error?: string }
     * Requirements: 10.1, 10.2, 10.3, 10.4
     */
    async getAllGames() {
        try {
            const result = await this.database.getAllGameEntries();
            return result;
        } catch (error) {
            return {
                success: false,
                error: `Failed to get all games: ${error.message}`
            };
        }
    }

    /**
     * Gets a single game entry by Steam ID
     * @param {string} steamID - Steam ID to look up
     * @returns {Promise<Object>} Result object { success: boolean, entry?: GameEntry, error?: string }
     * Requirements: 10.1, 10.2, 10.3, 10.4
     */
    async getGame(steamID) {
        try {
            const result = await this.database.getGameEntry(steamID);
            return result;
        } catch (error) {
            return {
                success: false,
                error: `Failed to get game: ${error.message}`
            };
        }
    }

    /**
     * Gets game data from Steam API by Steam ID (without adding to database)
     * @param {string} steamID - Steam ID to fetch from Steam API
     * @returns {Promise<Object>} Result object { success: boolean, gameData?: Object, error?: string }
     * Requirements: 10.1, 10.2, 10.3, 10.4
     */
    async getGameByID(steamID) {
        try {
            const result = await this.manager.fetchGameData(steamID);
            return result;
        } catch (error) {
            return {
                success: false,
                error: `Failed to fetch game data: ${error.message}`
            };
        }
    }

    /**
     * Updates user metadata for a game entry
     * @param {string} steamID - Steam ID of the game to update
     * @param {Object} metadata - User metadata to update
     * @returns {Promise<Object>} Result object { success: boolean, error?: string }
     * Requirements: 10.1, 10.2, 10.3, 10.4
     */
    async updateMetadata(steamID, metadata) {
        try {
            const result = await this.manager.updateUserMetadata(steamID, metadata);
            return result;
        } catch (error) {
            return {
                success: false,
                error: `Failed to update metadata: ${error.message}`
            };
        }
    }

    /**
     * Refresh a game's catalog metadata from its source API (Steam or RAWG).
     * Routes automatically based on the steamID prefix (rawg-* → RAWG, else → Steam).
     *
     * RAWG games use an authenticated fetch so the server can resolve the
     * per-user RAWG API key from user_secrets (unauthenticated RAWG client
     * requests can't be matched to a user and fall through to the unset env var).
     */
    async refreshGame(steamID) {
        try {
            let gameData;

            if (typeof steamID === 'string' && steamID.startsWith('rawg-')) {
                const rawgId = steamID.replace('rawg-', '');
                // Build auth headers using the same token the ProxyClient uses
                const token = await this.proxy._getToken?.();
                const headers = {};
                if (token) headers['Authorization'] = `Bearer ${token}`;
                const res = await fetch(`${this._apiBase}/rawg/games/${rawgId}`, { headers });
                if (!res.ok) {
                    const err = await res.json().catch(() => ({}));
                    return { success: false, error: err.error || `RAWG API error: ${res.status}` };
                }
                const raw = await res.json();
                // Parse into the same shape as RAWGAPIClient.parseDetailResponse
                gameData = {
                    steamID,
                    source: 'rawg',
                    title: raw.name || '',
                    imageURL: raw.background_image || raw.background_image_additional || '',
                    genres: (raw.genres || []).map((g) => g.name),
                    storeURL: `https://rawg.io/games/${raw.slug}`,
                    creator: raw.developers?.[0]?.name || '',
                    releaseDate: raw.released ? new Date(raw.released) : new Date(),
                    isDLC: false,
                };
            } else {
                const result = await this.manager.fetchGameData(steamID);
                if (!result.success) return result;
                gameData = result.gameData;
            }

            await this.database.updateSteamFields(steamID, gameData);
            return { success: true };
        } catch (error) {
            return { success: false, error: `Failed to refresh game: ${error.message}` };
        }
    }

    async deleteGame(steamID) {
        try {
            return await this.database.deleteGameEntry(steamID);
        } catch (error) {
            return { success: false, error: `Failed to delete game: ${error.message}` };
        }
    }

    /**
     * Gets user_games records for a single game (all users).
     * @param {string} gamePbId - PocketBase record ID
     * @returns {Promise<Object>} { records }
     */
    async getUserGamesForItem(gamePbId) {
        try {
            const records = await this.pocketbase.collection('user_games').getFullList({
                filter: `game="${gamePbId}"`,
                $autoCancel: false,
            });
            return { records };
        } catch (error) {
            return { records: [] };
        }
    }

    /**
     * Gets all user_games records from the join table.
     * Returns empty array if the collection doesn't exist yet (pre-migration).
     * @returns {Promise<Object>} { success, records }
     */
    async getAllUserGames() {
        try {
            const records = await this.pocketbase.collection('user_games').getFullList({
                $autoCancel: false,
                batch: 1000
            });
            return { success: true, records };
        } catch (error) {
            // Collection may not exist yet (pre-migration) — return empty
            return { success: true, records: [] };
        }
    }

    /**
     * Resolves a user name to a PocketBase record ID from tracker_users.
     * Caches results in this._userIdCache.
     * @param {string} userName - The user name to resolve
     * @returns {Promise<string>} The PB record ID
     */
    async resolveUserId(userName) {
        if (this._userIdCache.has(userName)) return this._userIdCache.get(userName);
        const record = await this.pocketbase.collection('tracker_users').getFirstListItem(`name="${userName}"`, { $autoCancel: false });
        this._userIdCache.set(userName, record.id);
        return record.id;
    }

    /**
     * Resolves a game's steamID to its PocketBase record ID.
     * Caches results in this._gamePbIdCache.
     * @param {string} steamID - The game's steamID
     * @returns {Promise<string>} The PB record ID
     */
    async resolveGamePbId(steamID) {
        if (this._gamePbIdCache.has(steamID)) return this._gamePbIdCache.get(steamID);
        const record = await this.pocketbase.collection('videogames').getFirstListItem(`steamID="${steamID}"`, { $autoCancel: false });
        this._gamePbIdCache.set(steamID, record.id);
        return record.id;
    }

    /**
     * Links a freshly added game to a user with sensible defaults. The timeline hides
     * games whose status is empty or "Not played", so a quick-add needs a real status
     * plus a last-played date (which also decides the year tab it lands under).
     * @param {string} userName - User name
     * @param {string} gamePbId - PocketBase record ID for the game
     * @returns {Promise<Object>} { success, error? }
     */
    async addToCollection(userName, gamePbId) {
        return this.upsertUserGame(userName, gamePbId, {
            status: GameStatus.PLAYING,
            lastPlayed: new Date().toISOString().split('T')[0],
        });
    }

    /**
     * Upserts a user_games join table record for a given user/game pair.
     * @param {string} userName - User name
     * @param {string} gamePbId - PocketBase record ID for the game
     * @param {Object} data - Fields to set: { status, rating, lastPlayed, completedDate, playTime, progress }
     * @returns {Promise<Object>} { success, error? }
     */
    async upsertUserGame(userName, gamePbId, data) {
        try {
            const userId = await this.resolveUserId(userName);

            // Clean nullish values
            const cleanData = {};
            for (const [key, value] of Object.entries(data)) {
                if (value !== undefined) cleanData[key] = value;
            }

            await this.proxy.collection('user_games').upsert(
                { game: gamePbId },
                cleanData,
                userId,
            );
            return { success: true };
        } catch (error) {
            return { success: false, error: `Failed to upsert user game: ${error.message}` };
        }
    }

    /**
     * Deletes a user_games join table record for a given user/game pair.
     * @param {string} userName - User name
     * @param {string} gamePbId - PocketBase record ID for the game
     * @returns {Promise<Object>} { success, error? }
     */
    /**
     * Update a game's main record (non-user fields like playedTogether).
     * @param {string} gamePbId - PocketBase record ID
     * @param {Object} updates - Fields to update on the videogames record
     * @returns {Promise<Object>} { success, error? }
     */
    async updateGame(gamePbId, updates) {
        try {
            await this.proxy.collection('videogames').update(gamePbId, updates);
            return { success: true };
        } catch (error) {
            return { success: false, error: `Failed to update game: ${error.message}` };
        }
    }

    async deleteUserGame(userName, gamePbId) {
        try {
            const userId = await this.resolveUserId(userName);
            const existing = await this.pocketbase.collection('user_games').getFirstListItem(
                `user="${userId}" && game="${gamePbId}"`, { $autoCancel: false }
            );
            await this.proxy.collection('user_games').delete(existing.id);
            return { success: true };
        } catch (error) {
            if (error.status === 404) return { success: true }; // already gone
            return { success: false, error: `Failed to delete user game: ${error.message}` };
        }
    }

    /**
     * Refreshes a single user's stats (playTime, lastPlayed) for a specific game
     * by re-fetching from their Steam library or Xbox stats API.
     * @param {string} gameID - The game's steamID (or xbox_titleId for Xbox games)
     * @param {string} user - User name
     * @param {string} userSteamId - The user's Steam64 ID (unused for Xbox games)
     * @param {Object} [options] - Additional options
     * @param {Object} [options.apiInfo] - Game's apiInfo object (contains Xbox titleId if synced)
     * @returns {Promise<Object>} { success, updated?: Object, error?: string }
     */
    async refreshUserStats(gameID, user, userSteamId, options = {}) {
        try {
            // Determine Xbox titleId — either from the xbox_ prefix or from apiInfo
            let xboxTitleId = null;
            if (gameID.startsWith('xbox_')) {
                xboxTitleId = gameID.replace('xbox_', '');
            } else if (options.apiInfo?.xbox?.titleId) {
                xboxTitleId = String(options.apiInfo.xbox.titleId);
            }

            if (xboxTitleId) {
                const statsResult = await getXboxGameStats(xboxTitleId, user);
                if (!statsResult.success) {
                    // If this is a cross-platform game, fall through to Steam
                    if (!gameID.startsWith('xbox_') && userSteamId) {
                        // Try Steam instead
                    } else {
                        return { success: false, error: statsResult.error || 'Failed to fetch Xbox stats' };
                    }
                } else {
                    const gamePbId = await this.resolveGamePbId(gameID);
                    const userUpdates = {};
                    if (statsResult.minutesPlayed != null) userUpdates.playTime = statsResult.minutesPlayed;

                    // For cross-platform games, also fetch Steam achievements
                    if (userSteamId && !gameID.startsWith('xbox_')) {
                        try {
                            const achResult = await getPlayerAchievements(userSteamId, gameID);
                            if (achResult.success) {
                                if (achResult.earned > 0) userUpdates.earnedAchievements = achResult.earned;
                                if (achResult.total > 0) userUpdates.totalAchievements = achResult.total;
                            }
                        } catch (_) { /* achievements fetch is best-effort */ }
                    }

                    if (Object.keys(userUpdates).length === 0) return { success: true, updated: {} };
                    return await this.upsertUserGame(user, gamePbId, userUpdates);
                }
            }

            // Steam games
            if (!userSteamId) {
                return { success: false, error: 'No Steam ID available for this user' };
            }
            const ownedResult = await getOwnedGames(userSteamId);
            if (!ownedResult.success) {
                return { success: false, error: ownedResult.error || 'Failed to fetch Steam library' };
            }
            const steamGame = ownedResult.data.games.find(
                (g) => String(g.appid) === String(gameID)
            );
            if (!steamGame) {
                return { success: false, error: 'Game not found in this user\'s Steam library' };
            }
            const gamePbId = await this.resolveGamePbId(gameID);
            const userUpdates = {};
            if (steamGame.lastPlayed) userUpdates.lastPlayed = steamGame.lastPlayed;
            if (steamGame.playtimeForever > 0) userUpdates.playTime = steamGame.playtimeForever;

            // Fetch Steam achievements
            try {
                const achResult = await getPlayerAchievements(userSteamId, gameID);
                if (achResult.success) {
                    if (achResult.earned > 0) userUpdates.earnedAchievements = achResult.earned;
                    if (achResult.total > 0) userUpdates.totalAchievements = achResult.total;
                }
            } catch (_) { /* achievements fetch is best-effort */ }

            if (Object.keys(userUpdates).length === 0) {
                return { success: true, updated: {} };
            }
            return await this.upsertUserGame(user, gamePbId, userUpdates);
        } catch (error) {
            return { success: false, error: `Failed to refresh stats: ${error.message}` };
        }
    }

    /**
     * Syncs a user's Steam library into the collection.
     * - Adds games not yet in the DB
     * - Updates the user's status via user_games join table
     *
     * @param {string} steamId - Steam64 ID
     * @param {string} user - User name
     * @param {function} onProgress - optional callback({ current, total, title })
     * @returns {Promise<Object>} { success, added, updated, skipped, failed, errors }
     */
    async syncFromSteam(steamId, user, onProgress) {
        try {
            // 1. Fetch owned games from Steam
            const ownedResult = await getOwnedGames(steamId);
            if (!ownedResult.success) {
                return { success: false, error: ownedResult.error };
            }

            const ownedGames = ownedResult.data.games;
            const results = { added: 0, updated: 0, skipped: 0, failed: 0, errors: [], addedList: [], updatedList: [], skippedList: [] };
            const total = ownedGames.length;

            // 2. Get all existing games from DB for quick lookup
            const existingResult = await this.getAllGames();
            const existingMap = new Map();
            if (existingResult.success && existingResult.entries) {
                for (const entry of existingResult.entries) {
                    existingMap.set(entry.steamID, entry);
                }
            }

            // 3. Process each owned game
            for (let i = 0; i < ownedGames.length; i++) {
                const steamGame = ownedGames[i];
                const steamID = steamGame.appid.toString();

                if (onProgress) {
                    onProgress({ current: i + 1, total, title: steamGame.name });
                }

                try {
                    const existing = existingMap.get(steamID);

                    if (existing) {
                        // Game exists — update user data via join table
                        const userUpdates = {};

                        // Check current status and ownership from user_games to respect protected statuses
                        let currentStatus = null;
                        let currentOwnership = [];
                        try {
                            const userId = await this.resolveUserId(user);
                            const userGame = await this.pocketbase.collection('user_games').getFirstListItem(
                                `user="${userId}" && game="${existing.id}"`, { $autoCancel: false }
                            );
                            currentStatus = userGame.status;
                            currentOwnership = userGame.ownership || [];
                        } catch (_) { /* no existing user_game record */ }

                        // Auto-add "steam" to ownership if not already present
                        if (!currentOwnership.includes('steam')) {
                            userUpdates.ownership = [...currentOwnership, 'steam'];
                        }

                        // Auto-set status unless user has a protected status
                        if (!PROTECTED_STATUSES.has(currentStatus)) {
                            userUpdates.status = inferStatus({
                                lastPlayed: steamGame.lastPlayed,
                                playtimeMinutes: steamGame.playtimeForever,
                            });
                        }

                        // Update lastPlayed and playTime
                        if (steamGame.lastPlayed) {
                            userUpdates.lastPlayed = steamGame.lastPlayed;
                        }
                        if (steamGame.playtimeForever > 0) {
                            userUpdates.playTime = steamGame.playtimeForever;
                        }

                        if (Object.keys(userUpdates).length > 0) {
                            const updateResult = await this.upsertUserGame(user, existing.id, userUpdates);
                            if (updateResult.success) {
                                results.updated++;
                                results.updatedList.push(steamGame.name);
                            } else {
                                results.failed++;
                                results.errors.push({ steamID, title: steamGame.name, error: updateResult.error });
                            }
                        } else {
                            results.skipped++;
                            results.skippedList.push(steamGame.name);
                        }
                    } else {
                        // Skip games with less than 10 minutes of playtime
                        if (steamGame.playtimeForever < 10) {
                            results.skipped++;
                            results.skippedList.push(steamGame.name);
                            continue;
                        }

                        // New game — fetch details from Steam API with retry on 403
                        let gameResult = null;
                        for (let attempt = 0; attempt < 3; attempt++) {
                            gameResult = await getGameByID(steamID);
                            if (gameResult.success || !gameResult.error?.includes('403')) break;
                            // Steam rate limit — back off and retry
                            await new Promise(r => setTimeout(r, 3000 * (attempt + 1)));
                        }

                        if (!gameResult.success) {
                            results.failed++;
                            results.errors.push({ steamID, title: steamGame.name, error: gameResult.error || 'Failed to fetch' });
                            await new Promise(r => setTimeout(r, 1500));
                            continue;
                        }

                        // Create the game entry (no user-prefixed fields on the game record)
                        const gameData = gameResult.data;

                        const addResult = await this.addGameFromData(gameData);
                        if (addResult.success) {
                            // Write user data to join table
                            const gamePbId = addResult.pbId || await this.resolveGamePbId(steamID);
                            const userUpdates = {};
                            userUpdates.status = inferStatus({
                                lastPlayed: steamGame.lastPlayed,
                                playtimeMinutes: steamGame.playtimeForever,
                            });
                            if (steamGame.lastPlayed) userUpdates.lastPlayed = steamGame.lastPlayed;
                            if (steamGame.playtimeForever > 0) userUpdates.playTime = steamGame.playtimeForever;
                            userUpdates.ownership = ['steam'];
                            // A game that isn't linked to the user never shows up for them,
                            // so a failed link is a failed sync entry, not an add.
                            const linkResult = await this.upsertUserGame(user, gamePbId, userUpdates);
                            if (linkResult.success) {
                                results.added++;
                                results.addedList.push(steamGame.name);
                            } else {
                                results.failed++;
                                results.errors.push({ steamID, title: steamGame.name, error: linkResult.error });
                            }
                        } else if (addResult.error?.includes('already')) {
                            results.skipped++;
                            results.skippedList.push(steamGame.name);
                        } else {
                            results.failed++;
                            results.errors.push({ steamID, title: steamGame.name, error: addResult.error });
                        }

                        // Rate-limit delay between new game fetches (1.5s to avoid Steam 403)
                        await new Promise(r => setTimeout(r, 1500));
                    }
                } catch (err) {
                    results.failed++;
                    results.errors.push({ steamID, title: steamGame.name, error: err.message });
                }
            }

            // 4. Remove "steam" ownership from games no longer in Steam library
            try {
                const ownedSteamIds = new Set(ownedGames.map((g) => g.appid.toString()));
                const userId = await this.resolveUserId(user);
                const userGameRecords = await this.pocketbase.collection('user_games').getFullList({
                    filter: `user="${userId}"`,
                    $autoCancel: false,
                });
                for (const ugr of userGameRecords) {
                    const ownership = ugr.ownership || [];
                    if (ownership.includes('steam')) {
                        try {
                            const gameRecord = await this.pocketbase.collection('videogames').getOne(ugr.game, { $autoCancel: false });
                            // Only check non-xbox games (xbox games have steamID starting with 'xbox_')
                            if (gameRecord && !gameRecord.steamID?.startsWith('xbox_') && !ownedSteamIds.has(gameRecord.steamID)) {
                                const newOwnership = ownership.filter((p) => p !== 'steam');
                                await this.proxy.collection('user_games').update(ugr.id, {
                                    ownership: newOwnership,
                                });
                            }
                        } catch (_) { /* game record not found, skip */ }
                    }
                }
            } catch (err) {
                // Non-critical — don't fail the entire sync
                console.warn('Ownership cleanup failed:', err.message);
            }

            return { success: true, ...results };
        } catch (error) {
            return { success: false, error: `Steam sync failed: ${error.message}` };
        }
    }

    /**
     * Syncs games from Xbox Live via OpenXBL.
     * Matches Xbox titles to existing DB entries by name (case-insensitive).
     * Creates new entries for unmatched titles. Updates user data via join table.
     *
     * @param {string} gamertag - Xbox gamertag
     * @param {string} user - User name
     * @param {function} onProgress - optional callback({ current, total, title })
     * @returns {Promise<Object>} { success, added, updated, skipped, failed, errors }
     */
    async syncFromXbox(gamertag, user, onProgress) {
        try {
            // 1. Fetch title history from Xbox
            const historyResult = await getXboxTitleHistory(gamertag, user);
            if (!historyResult.success) {
                return { success: false, error: historyResult.error };
            }

            const xboxGames = historyResult.data.games;
            const results = { added: 0, updated: 0, skipped: 0, failed: 0, errors: [], addedList: [], updatedList: [], skippedList: [] };
            const total = xboxGames.length;
            const inferXboxGameStatus = (xboxGame) => inferStatus({
                lastPlayed: xboxGame.lastPlayed,
                playtimeMinutes: xboxGame.minutesPlayed || 0,
                isXbox: true,
                achievementPct: xboxGame._raw?.achievement?.progressPercentage ?? 0,
            });

            // 2. Get all existing games from DB for matching
            const existingResult = await this.getAllGames();
            const existingByTitle = new Map(); // title (lowercase) → entry
            const existingBySteamID = new Map();
            if (existingResult.success && existingResult.entries) {
                for (const entry of existingResult.entries) {
                    existingBySteamID.set(entry.steamID, entry);
                    if (entry.title) {
                        existingByTitle.set(entry.title.toLowerCase().trim(), entry);
                    }
                }
            }

            // 3. Process each Xbox game
            for (let i = 0; i < xboxGames.length; i++) {
                const xboxGame = xboxGames[i];

                if (onProgress) {
                    onProgress({ current: i + 1, total, title: xboxGame.name });
                }

                try {
                    // Try to match by title (case-insensitive)
                    const titleKey = xboxGame.name.toLowerCase().trim();
                    const existing = existingByTitle.get(titleKey);

                    if (existing) {
                        // Game exists — update user data via join table
                        const userUpdates = {};

                        // Check current user_games record for protected statuses & existing lastPlayed
                        let currentStatus = null;
                        let currentLastPlayed = null;
                        let currentOwnership = [];
                        try {
                            const userId = await this.resolveUserId(user);
                            const userGame = await this.pocketbase.collection('user_games').getFirstListItem(
                                `user="${userId}" && game="${existing.id}"`, { $autoCancel: false }
                            );
                            currentStatus = userGame.status;
                            currentLastPlayed = userGame.lastPlayed;
                            currentOwnership = userGame.ownership || [];
                        } catch (_) { /* no existing user_game record */ }

                        // Auto-add "xbox" to ownership
                        if (!currentOwnership.includes('xbox')) {
                            userUpdates.ownership = [...currentOwnership, 'xbox'];
                        }

                        if (xboxGame.lastPlayed) {
                            userUpdates.lastPlayed = xboxGame.lastPlayed;
                        }

                        // Auto-set status unless user has a protected status
                        if (!PROTECTED_STATUSES.has(currentStatus)) {
                            userUpdates.status = inferXboxGameStatus(xboxGame);
                        }

                        // Fetch MinutesPlayed from Xbox stats API
                        try {
                            const statsResult = await getXboxGameStats(xboxGame.titleId, user);
                            if (statsResult.success && statsResult.minutesPlayed != null) {
                                userUpdates.playTime = statsResult.minutesPlayed;
                            }
                        } catch (_) { /* stats fetch is best-effort */ }

                        // Achievement progress & counts
                        const progressPct = xboxGame._raw?.achievement?.progressPercentage;
                        if (progressPct != null) userUpdates.progress = progressPct;
                        if (xboxGame.earnedAchievements != null) userUpdates.earnedAchievements = xboxGame.earnedAchievements;
                        if (xboxGame.totalAchievements != null) userUpdates.totalAchievements = xboxGame.totalAchievements;

                        // Write user data to join table
                        if (Object.keys(userUpdates).length > 0) {
                            const upsertResult = await this.upsertUserGame(user, existing.id, userUpdates);
                            if (!upsertResult.success) {
                                results.failed++;
                                results.errors.push({ steamID: existing.steamID, title: xboxGame.name, error: upsertResult.error });
                                continue;
                            }
                        }

                        // Store/update Xbox API data on the game record (non-user field)
                        const gameUpdates = {};
                        gameUpdates.apiInfo = { ...existing.apiInfo, xbox: xboxGame._raw };

                        // Promote source to 'xbox' if Xbox has a more recent lastPlayed
                        // (indicates the game is actively played on Xbox, not Steam)
                        if (xboxGame.lastPlayed && existing.source !== 'xbox') {
                            const xboxDate = new Date(xboxGame.lastPlayed).getTime();
                            const existingDate = currentLastPlayed ? new Date(currentLastPlayed).getTime() : 0;
                            if (xboxDate >= existingDate) {
                                gameUpdates.source = 'xbox';
                            }
                        }

                        if (Object.keys(gameUpdates).length > 0) {
                            const updateResult = await this.updateMetadata(existing.steamID, gameUpdates);
                            if (updateResult.success) {
                                results.updated++;
                                results.updatedList.push(xboxGame.name);
                            } else {
                                results.failed++;
                                results.errors.push({ steamID: existing.steamID, title: xboxGame.name, error: updateResult.error });
                            }
                        } else {
                            results.updated++;
                            results.updatedList.push(xboxGame.name);
                        }
                    } else {
                        // New game — create entry from Xbox data
                        // Skip titles with 0 achievements (likely apps, not games)
                        if (xboxGame.earnedAchievements === 0 && !xboxGame.lastPlayed) {
                            results.skipped++;
                            results.skippedList.push(xboxGame.name);
                            continue;
                        }

                        // Enrich with RAWG metadata (genres, creator, image, release date)
                        let rawgData = null;
                        try {
                            const rawgResult = await searchGamesByQuery(xboxGame.name);
                            if (rawgResult.success && rawgResult.data.games.length > 0) {
                                // Pick the best match — exact title match first, otherwise first result
                                const nameLC = xboxGame.name.toLowerCase().trim();
                                rawgData = rawgResult.data.games.find(
                                    (g) => g.title.toLowerCase().trim() === nameLC
                                ) || rawgResult.data.games[0];
                            }
                        } catch (_) { /* RAWG enrichment is best-effort */ }

                        const xboxSteamID = `xbox_${xboxGame.titleId}`;
                        const gameData = {
                            steamID: xboxSteamID,
                            source: 'xbox',
                            title: xboxGame.name,
                            imageURL: rawgData?.imageURL || xboxGame.imageUrl || '',
                            genres: rawgData?.genres || [],
                            storeURL: rawgData?.storeURL || '',
                            creator: rawgData?.creator || '',
                            releaseDate: rawgData?.releaseDate || null,
                            isDLC: false,
                        };

                        const addResult = await this.addGameFromData(gameData);
                        if (addResult.success) {
                            // Write user data to join table
                            const gamePbId = addResult.pbId || await this.resolveGamePbId(xboxSteamID);
                            const userUpdates = {};
                            userUpdates.status = inferXboxGameStatus(xboxGame);
                            if (xboxGame.lastPlayed) userUpdates.lastPlayed = xboxGame.lastPlayed;
                            const progressPct = xboxGame._raw?.achievement?.progressPercentage;
                            if (progressPct != null) userUpdates.progress = progressPct;
                            if (xboxGame.earnedAchievements != null) userUpdates.earnedAchievements = xboxGame.earnedAchievements;
                            if (xboxGame.totalAchievements != null) userUpdates.totalAchievements = xboxGame.totalAchievements;
                            // Fetch MinutesPlayed from Xbox stats API
                            try {
                                const statsResult = await getXboxGameStats(xboxGame.titleId, user);
                                if (statsResult.success && statsResult.minutesPlayed != null) {
                                    userUpdates.playTime = statsResult.minutesPlayed;
                                }
                            } catch (_) { /* stats fetch is best-effort */ }
                            userUpdates.ownership = ['xbox'];
                            const linkResult = await this.upsertUserGame(user, gamePbId, userUpdates);
                            if (linkResult.success) {
                                results.added++;
                                results.addedList.push(xboxGame.name);
                            } else {
                                results.failed++;
                                results.errors.push({ steamID: xboxSteamID, title: xboxGame.name, error: linkResult.error });
                            }
                            // Store Xbox API data on the game record (non-user field)
                            await this.updateMetadata(xboxSteamID, { apiInfo: { xbox: xboxGame._raw } }).catch(() => {});
                        } else if (addResult.error?.includes('already')) {
                            results.skipped++;
                            results.skippedList.push(xboxGame.name);
                        } else {
                            results.failed++;
                            results.errors.push({ steamID: gameData.steamID, title: xboxGame.name, error: addResult.error });
                        }
                    }
                } catch (err) {
                    results.failed++;
                    results.errors.push({ steamID: xboxGame.titleId, title: xboxGame.name, error: err.message });
                }
            }

            return { success: true, ...results };
        } catch (error) {
            return { success: false, error: `Xbox sync failed: ${error.message}` };
        }
    }

    /**
     * Enriches Xbox games that are missing genres/creator by searching RAWG
     * @param {function} onProgress - optional callback({ current, total, title })
     * @returns {Promise<Object>} { success, enriched, skipped, failed, errors }
     */
    async enrichXboxGames(onProgress) {
        try {
            const allResult = await this.getAllGames();
            if (!allResult.success) return { success: false, error: allResult.error };

            // Find Xbox games missing genres, creator, or still using Xbox portrait images
            const xboxGames = (allResult.entries || []).filter(
                (e) => e.source === 'xbox' && (
                    !e.genres?.length || !e.creator ||
                    e.imageURL?.includes('store-images.s-microsoft.com')
                )
            );
            const results = { enriched: 0, skipped: 0, failed: 0, errors: [], enrichedList: [], skippedList: [] };
            const total = xboxGames.length;

            for (let i = 0; i < xboxGames.length; i++) {
                const game = xboxGames[i];
                if (onProgress) onProgress({ current: i + 1, total, title: game.title });

                try {
                    const rawgResult = await searchGamesByQuery(game.title);
                    if (!rawgResult.success || !rawgResult.data.games.length) {
                        results.skipped++;
                        results.skippedList.push(game.title);
                        continue;
                    }

                    const nameLC = game.title.toLowerCase().trim();
                    const match = rawgResult.data.games.find(
                        (g) => g.title.toLowerCase().trim() === nameLC
                    ) || rawgResult.data.games[0];

                    const updates = {};
                    if (!game.genres?.length && match.genres?.length) updates.genres = match.genres;
                    if (!game.creator && match.creator) updates.creator = match.creator;
                    // Always prefer RAWG/Steam landscape images over Xbox portrait images
                    const hasXboxImage = game.imageURL?.includes('store-images.s-microsoft.com');
                    if (match.imageURL && (!game.imageURL || hasXboxImage)) updates.imageURL = match.imageURL;
                    if (!game.releaseDate && match.releaseDate) updates.releaseDate = match.releaseDate;
                    if (!game.storeURL && match.storeURL) updates.storeURL = match.storeURL;

                    if (Object.keys(updates).length > 0) {
                        const updateResult = await this.updateMetadata(game.steamID, updates);
                        if (updateResult.success) {
                            results.enriched++;
                            results.enrichedList.push(game.title);
                        } else {
                            results.failed++;
                            results.errors.push({ title: game.title, error: updateResult.error });
                        }
                    } else {
                        results.skipped++;
                        results.skippedList.push(game.title);
                    }
                } catch (err) {
                    results.failed++;
                    results.errors.push({ title: game.title, error: err.message });
                }
            }

            return { success: true, ...results };
        } catch (error) {
            return { success: false, error: `Enrichment failed: ${error.message}` };
        }
    }

    /**
     * Refreshes Steam API data for all games in the collection
     * @returns {Promise<Object>} Result object { success: boolean, updated: number, failed: number, errors?: Array }
     */
    async refreshAllGames() {
        try {
            // Get all games
            const gamesResult = await this.getAllGames();
            if (!gamesResult.success) {
                return {
                    success: false,
                    error: gamesResult.error
                };
            }

            const games = gamesResult.entries;
            const results = {
                updated: 0,
                failed: 0,
                errors: []
            };

            // Update each game with fresh API data (source-aware)
            for (const game of games) {
                try {
                    const steamResult = await this.manager.fetchGameData(game.steamID);

                    if (!steamResult.success) {
                        results.failed++;
                        results.errors.push({
                            steamID: game.steamID,
                            title: game.title,
                            error: steamResult.error
                        });
                        continue;
                    }

                    // Update only Steam API fields (preserve user metadata)
                    const updateResult = await this.database.updateSteamFields(
                        game.steamID,
                        steamResult.gameData
                    );

                    if (updateResult.success) {
                        results.updated++;
                    } else {
                        results.failed++;
                        results.errors.push({
                            steamID: game.steamID,
                            title: game.title,
                            error: updateResult.error
                        });
                    }

                    // Small delay to avoid overwhelming the API
                    await new Promise(resolve => setTimeout(resolve, 500));
                } catch (error) {
                    results.failed++;
                    results.errors.push({
                        steamID: game.steamID,
                        title: game.title,
                        error: error.message
                    });
                }
            }

            return {
                success: true,
                updated: results.updated,
                failed: results.failed,
                errors: results.errors
            };
        } catch (error) {
            return {
                success: false,
                error: `Failed to refresh games: ${error.message}`
            };
        }
    }

    // ── Lists CRUD ──────────────────────────────────────────────────────────────

    async getAllLists() {
        try {
            const headers = await this._authHeaders();
            const res = await fetch(`${this._apiBase}/pb/lists?app=videogames`, { headers });
            if (!res.ok) {
                const err = await res.json().catch(() => ({}));
                console.error('[BackendService] getAllLists failed:', res.status, err);
                return { success: false, lists: [] };
            }
            return res.json();
        } catch (error) {
            console.error('[BackendService] getAllLists error:', error);
            return { success: false, lists: [] };
        }
    }

    async createList(_, { name, emoji }) {
        // Server injects the authenticated user's ID via USER_FIELD_MAP — no need to resolve it here
        return this.proxy.collection('lists').create({
            name, emoji: emoji || '', app: 'videogames',
        });
    }

    async updateList(listId, data) {
        return this.proxy.collection('lists').update(listId, data);
    }

    async deleteList(listId) {
        // Delete all items in the list first
        const items = await this.pocketbase.collection('list_items').getFullList({
            filter: `list="${listId}"`, $autoCancel: false,
        });
        for (const item of items) {
            await this.proxy.collection('list_items').delete(item.id);
        }
        await this.proxy.collection('lists').delete(listId);
    }

    // ── List Items CRUD ─────────────────────────────────────────────────────────

    async getListItems(listId) {
        try {
            const headers = await this._authHeaders();
            const res = await fetch(`${this._apiBase}/pb/list-items?list=${encodeURIComponent(listId)}`, { headers });
            if (!res.ok) {
                const err = await res.json().catch(() => ({}));
                console.error('[BackendService] getListItems failed:', res.status, err);
                return { success: false, items: [] };
            }
            return res.json();
        } catch (error) {
            console.error('[BackendService] getListItems error:', error);
            return { success: false, items: [] };
        }
    }

    async addListItem(listId, { itemId, title, imageURL, notes }) {
        // Get next position
        const existing = await this.pocketbase.collection('list_items').getFullList({
            filter: `list="${listId}"`, sort: '-position', $autoCancel: false,
        });
        const nextPos = existing.length > 0 ? (existing[0].position || 0) + 1 : 0;

        return this.proxy.collection('list_items').create({
            list: listId,
            itemId: itemId || '',
            title,
            imageURL: imageURL || '',
            notes: notes || '',
            position: nextPos,
        });
    }

    async updateListItem(id, data) {
        return this.proxy.collection('list_items').update(id, data);
    }

    async deleteListItem(id) {
        await this.proxy.collection('list_items').delete(id);
    }
}

export default BackendService;
